import React from 'react';
import { api } from '@/lib/api';
import {
  Spinner, ArrowsClockwise, ShieldWarning, CheckCircle,
  Scales, WarningOctagon, ShieldCheck, Question,
} from '@phosphor-icons/react';

const SEV_COLORS = {
  critical: 'bg-red-50 text-red-800 border-red-200',
  major: 'bg-orange-50 text-orange-800 border-orange-200',
  minor: 'bg-yellow-50 text-yellow-800 border-yellow-200',
};

const MATERIALITY_COLORS = {
  high: 'bg-red-50 text-red-700 border-red-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-gray-100 text-gray-500 border-gray-200',
};

const ContradictionCard = ({ c, index, onVerify }) => (
  <div className={`bg-white border rounded-lg p-5 ${c.verified ? 'border-green-200' : 'border-[#E5E7EB]'}`} data-testid={`contradiction-${index}`}>
    <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs font-bold px-2 py-0.5 rounded border ${SEV_COLORS[c.severity] || SEV_COLORS.minor}`}>{(c.severity || 'minor').toUpperCase()}</span>
        {c.materiality && (
          <span className={`text-[11px] font-ibm font-semibold px-2 py-0.5 rounded border ${MATERIALITY_COLORS[c.materiality] || MATERIALITY_COLORS.medium}`} data-testid={`contra-materiality-${index}`}>
            Materiality: {c.materiality}
          </span>
        )}
        {c.contradiction_type && <span className="text-xs text-[#6B7280] font-ibm capitalize">{c.contradiction_type.replace(/_/g, ' ')}</span>}
        {c.verified && <span className="text-xs px-1.5 py-0.5 bg-green-50 text-green-700 rounded font-ibm flex items-center gap-0.5"><CheckCircle size={10} /> Verified</span>}
      </div>
      <button onClick={() => onVerify(c._id, c.verified)} data-testid={`verify-contra-${index}`}
        className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-md font-ibm transition-all ${c.verified ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB]'}`}>
        <CheckCircle size={12} weight={c.verified ? 'fill' : 'regular'} /> {c.verified ? 'Verified' : 'Verify'}
      </button>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
      <div className="p-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-md">
        <p className="text-xs font-bold text-[#6B7280] font-ibm mb-1">{c.document_a_name}</p>
        <p className="text-sm text-[#09090B] font-ibm">{c.claim_a_text}</p>
      </div>
      <div className="p-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-md">
        <p className="text-xs font-bold text-[#6B7280] font-ibm mb-1">{c.document_b_name}</p>
        <p className="text-sm text-[#09090B] font-ibm">{c.claim_b_text}</p>
      </div>
    </div>

    {c.ai_explanation && (
      <div className="p-3 bg-[#F9FAFB] rounded-md border border-[#E5E7EB] mb-3">
        <p className="text-xs font-bold text-[#6B7280] font-ibm mb-1 flex items-center gap-1"><Scales size={12} /> Neutral summary</p>
        <p className="text-sm text-[#09090B] font-ibm">{c.ai_explanation}</p>
      </div>
    )}

    {/* Two-sided analysis — the record never overstates a contradiction */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className="p-3 bg-red-50/50 border border-red-100 rounded-md" data-testid={`contra-why-conflict-${index}`}>
        <p className="text-xs font-bold text-red-700 font-ibm mb-1 flex items-center gap-1"><WarningOctagon size={12} weight="fill" /> Why it may conflict</p>
        <p className="text-sm text-[#09090B] font-ibm">{c.why_may_conflict || '—'}</p>
      </div>
      <div className="p-3 bg-green-50/50 border border-green-100 rounded-md" data-testid={`contra-why-not-conflict-${index}`}>
        <p className="text-xs font-bold text-green-700 font-ibm mb-1 flex items-center gap-1"><ShieldCheck size={12} weight="fill" /> Why it may NOT conflict</p>
        <p className="text-sm text-[#09090B] font-ibm">{c.why_may_not_conflict || '—'}</p>
      </div>
    </div>

    {c.clarifying_question && (
      <div className="mt-3 p-3 bg-blue-50/60 border border-blue-100 rounded-md flex items-start gap-2" data-testid={`contra-clarifying-${index}`}>
        <Question size={16} className="text-blue-600 mt-0.5 shrink-0" weight="fill" />
        <div>
          <p className="text-xs font-bold text-blue-700 font-ibm mb-0.5">Question to resolve this</p>
          <p className="text-sm text-[#09090B] font-ibm">{c.clarifying_question}</p>
        </div>
      </div>
    )}
  </div>
);

export const ContradictionsTab = ({ contradictions, runContradictions, detectingContras, analyzedDocs, loadAll }) => {
  const toggleVerify = async (findingId, current) => {
    try {
      await api.put(`/contradictions/${findingId}/verify`, { verified: !current });
      await loadAll();
    } catch {
      alert('Failed to update');
    }
  };

  return (
    <div data-testid="contradictions-tab">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-outfit text-lg font-semibold text-[#09090B]">Contradiction Findings ({contradictions.length})</h3>
        <button data-testid="detect-contradictions-button" onClick={runContradictions} disabled={detectingContras || analyzedDocs.length < 2}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#002FA7] text-white font-ibm font-semibold rounded-md text-sm hover:bg-[#00237D] disabled:opacity-50 transition-all">
          {detectingContras ? <><Spinner size={14} className="animate-spin" /> Detecting...</> : <><ArrowsClockwise size={14} /> Run Detection</>}
        </button>
      </div>
      <p className="text-sm text-[#6B7280] font-ibm mb-4 max-w-2xl">
        Each finding shows both sides — why the statements may conflict AND why they may not — plus a question that would resolve it. Nothing is asserted as a proven contradiction.
      </p>
      {analyzedDocs.length < 2 && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-700 font-ibm">
          At least 2 analyzed documents are needed for contradiction detection.
        </div>
      )}
      {contradictions.length === 0 ? (
        <div className="text-center py-16 bg-white border border-dashed border-[#E5E7EB] rounded-lg">
          <ShieldWarning size={48} className="mx-auto text-[#E5E7EB] mb-3" />
          <p className="text-[#6B7280] font-ibm">No contradictions detected yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {contradictions.map((c, i) => (
            <ContradictionCard key={c._id || `contra-${i}`} c={c} index={i} onVerify={toggleVerify} />
          ))}
        </div>
      )}
    </div>
  );
};
